/**
 * Sammelaktionen der Akte: bündelt die Einzel-Endpoints (Move/Delete/Reclassify/Download) über
 * `Promise.allSettled` und fährt **ein** `invalidate()` am Ende (nicht pro Datei → nur ein
 * Refetch). Gesperrte Belege werden vor jeder Mutation herausgefiltert. Move/Delete laufen
 * optimistisch über den Tree-Cache; der abschließende Refetch ist zugleich der Teil-Rollback —
 * fehlgeschlagene Dateien tauchen wieder auf, erfolgreiche bleiben verschoben/gelöscht.
 */
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type PropertyDocumentKind,
  type PropertyFileResponse,
  type PropertyFileTreeNode,
} from '@/shared/api';
import { akteErrorMessage } from './akteErrorMessages';
import { bulkResultText, partitionLocked } from './akteBulkRules';
import { invalidatePropertyFiles } from './akteTreeCache';
import { moveFileInTree, removeFileFromTree } from './akteTreeOps';

/** Beleg-Datum je Datei für Bulk-Reclassify (vorhandenes behalten, sonst gemeinsames Datum). */
export type IssuedOnResolver = (file: PropertyFileResponse) => string | null;

/** Stößt den Browser-Download eines Blobs an; Revoke verzögert, sonst bricht der Save ab. */
function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function useAkteBulkActions(propertyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const treeKey = ['propertyFiles', 'tree', propertyId] as const;
  const invalidate = () => invalidatePropertyFiles(qc, propertyId);

  function report(verb: string, ok: number, locked: number, fail: number) {
    if (fail > 0) {
      toast.error(bulkResultText(verb, { ok, locked, fail }));
      return;
    }
    // Nichts verarbeitet (Auswahl war komplett gesperrt oder bereits am Ziel) → neutraler Hinweis
    // statt eines grünen „0 verschoben".
    if (ok === 0) {
      if (locked > 0) toast(`${locked} gesperrt übersprungen`);
      return;
    }
    toast.success(bulkResultText(verb, { ok, locked, fail }));
  }

  /** allSettled über `items`, dann invalidate + Ergebnis-Toast; optional optimistisches Tree-Update. */
  async function run<T>(
    items: T[],
    lockedCount: number,
    verb: string,
    call: (item: T) => Promise<unknown>,
    optimistic?: (t: PropertyFileTreeNode) => PropertyFileTreeNode,
  ) {
    if (items.length === 0 && lockedCount === 0) return;
    let prev: PropertyFileTreeNode | undefined;
    if (optimistic && items.length > 0) {
      await qc.cancelQueries({ queryKey: treeKey });
      prev = qc.getQueryData<PropertyFileTreeNode>(treeKey);
      if (prev) qc.setQueryData(treeKey, optimistic(prev));
    }
    const results = await Promise.allSettled(items.map(call));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = items.length - ok;
    // Bei (Teil-)Fehler das optimistische Update zurücknehmen; der Refetch stellt die Wahrheit
    // her (erfolgreiche bleiben verschoben/gelöscht, fehlgeschlagene tauchen wieder auf).
    // Scheitert der Refetch, zeigt der Baum den Vor-Zustand statt fälschlich „alles weg".
    if (prev && fail > 0) qc.setQueryData(treeKey, prev);
    invalidate();
    report(verb, ok, lockedCount, fail);
  }

  return {
    /** Verschiebt alle nicht gesperrten Dateien der Auswahl in `targetFolder` (gleiche Kategorie). */
    move: (files: PropertyFileResponse[], targetFolder: string) => {
      const { mutable, locked } = partitionLocked(files);
      const toMove = mutable.filter((f) => f.folder !== targetFolder);
      return run(
        toMove,
        locked.length,
        'verschoben',
        (f) => api.propertyFiles.move(propertyId, f.id, { targetFolder }),
        (t) => toMove.reduce((acc, f) => moveFileInTree(acc, f.id, targetFolder), t),
      );
    },

    /** Soft-Delete für alle nicht gesperrten Dateien der Auswahl. */
    remove: (files: PropertyFileResponse[]) => {
      const { mutable, locked } = partitionLocked(files);
      return run(
        mutable,
        locked.length,
        'gelöscht',
        (f) => api.propertyFiles.delete(propertyId, f.id),
        (t) => mutable.reduce((acc, f) => removeFileFromTree(acc, f.id), t),
      );
    },

    /** Umklassifiziert alle nicht gesperrten Dateien auf `kind`; Datum je Datei über `issuedOn`. */
    reclassify: (
      files: PropertyFileResponse[],
      kind: PropertyDocumentKind,
      issuedOn: IssuedOnResolver,
    ) => {
      const { mutable, locked } = partitionLocked(files);
      return run(mutable, locked.length, 'umklassifiziert', (f) =>
        api.propertyFiles.reclassify(propertyId, f.id, { kind, issuedOn: issuedOn(f) }),
      );
    },

    /**
     * Lädt die Auswahl herunter (auch gesperrte — reiner Read): eine Datei direkt, mehrere als ein
     * ZIP über den Sammel-Endpoint (Ordnerstruktur erhalten, ein Request statt N).
     */
    download: async (files: PropertyFileResponse[]) => {
      if (files.length === 0) return;
      try {
        if (files.length === 1) {
          const blob = await api.propertyFiles.download(propertyId, files[0]!.id);
          triggerBlobDownload(blob, files[0]!.fileName);
        } else {
          const blob = await api.propertyFiles.downloadZip(
            propertyId,
            files.map((f) => f.id),
          );
          triggerBlobDownload(blob, `Akte-${files.length}-Dateien.zip`);
        }
      } catch (err) {
        toast.error(akteErrorMessage(await toApiError(err)));
      }
    },
  };
}
